import React from "react";
import {
	Button,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	FormGroup,
	Label,
	Input
} from "reactstrap";
import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import {
	ACQUISITION_KIOSK_MODE,
	buildAcquisitionFormsPath,
	buildAcquisitionKioskPath,
	getAcquisitionRouteId,
	setAcquisitionKioskSession
} from "constants/AcquisitionKiosk";

const FORM_OPTIONS = [
	{key: "ri060", label: "Michigan RI-060"}
];
const SIGNATURE_PREFERENCES = [
	{key: "print_both", label: "Print both (with and without signature)"},
	{key: "print_signature", label: "Print signature only"},
	{key: "no_signature", label: "No signature"}
];
const DEFAULT_SIGNATURE_PREFERENCE = "print_signature";

class FormPicker extends React.Component {
	constructor(props) {
		super(props);

		this.lockModel = new API.BuildClass(Types.Accounts._name, API.RequestType.READ);
		this.pinRefs = [
			React.createRef(),
			React.createRef(),
			React.createRef(),
			React.createRef()
		];

		this.state = {
			setupModalOpen: false,
			selectedForm: "",
			signaturePreference: DEFAULT_SIGNATURE_PREFERENCE,
			location: "",
			location_binary_id: "",
			location_error: "",
			pin_1: "",
			pin_2: "",
			pin_3: "",
			pin_4: "",
			error: "",
			hasAccountPin: false,
			isLoading: false
		};
	}

	handleSelectPropertyUpdate = (property, value, linked, object) => {
		if (linked) {
			if (object && object.hasOwnProperty("binary_id")) {
				this.setState({
					[linked]: object,
					[property]: value,
					[property + "_error"]: "",
					[property + "_binary_id"]: object.binary_id,
					[property + "_binary_id_error"]: "",
				});
			} else {
				this.setState({
					[linked]: object,
					[property]: value,
					[property + "_error"]: "",
				});
			}
		} else {
			this.setState({
				[property]: value,
				[property + "_error"]: "",
			});
		}
	};

	getCollectionIdentifiers = () => {
		const collection = this.props.row?.collection ?? this.props.row?.pkey;
		const collection_binary_id = this.props.row?.collection_binary_id ?? this.props.row?.binary_id;
		return {collection, collection_binary_id};
	};

	navigateTo = (pathname) => {
		if (this.props.history?.push) {
			this.props.history.push(pathname);
			return;
		}

		window.location.href = pathname;
	};

	createRi060Form = async () => {
		const {location, location_binary_id, signaturePreference} = this.state;
		const {collection, collection_binary_id} = this.getCollectionIdentifiers();
		const routeId = getAcquisitionRouteId({collection, collection_binary_id});

		if (!routeId) {
			this.setState({
				error: "Missing transaction identifiers for form generation",
				isLoading: false
			});
			return false;
		}

		try {
			const formsModel = new API.BuildClass(Types.Transactions_Collections_Forms._name, API.RequestType.CREATE);
			formsModel.parent = routeId;
			formsModel.object.form = 26;
			formsModel.object.send_email = false;
			formsModel.object.location = location;
			formsModel.object.location_binary_id = location_binary_id;
			formsModel.object.ri060_signature_preference = signaturePreference;

			await formsModel.submit(null, API.RequestType.CREATE);

			this.resetState();
			this.navigateTo(buildAcquisitionFormsPath(routeId));
			return true;
		} catch (err) {
			this.setState({
				error: "Failed to generate form",
				isLoading: false
			});
			return false;
		}
	};

	checkAccountPin = () => {
		const hasPin = !!this.props.rootState?.hasPin;
		this.setState({hasAccountPin: hasPin});
	};

	isDealerEntity = () => {
		return !!this.props.row?.dealer;
	};

	isCustomerEntity = () => {
		return !!this.props.row?.customer;
	};

	hasRIModule = () => {
		const {rootState} = this.props;
		return !!(rootState?.hasRI060AModule || rootState?.hasRIComboModule);
	};

	handleLockButtonClick = () => {
		if (this.state.setupModalOpen || this.state.isLoading) {
			return;
		}

		this.checkAccountPin();
		const defaultForm = this.hasRIModule() ? "ri060" : "";

		this.setState({
			setupModalOpen: true,
			selectedForm: defaultForm,
			signaturePreference: DEFAULT_SIGNATURE_PREFERENCE,
			error: ""
		});
	};

	handlePinChange = (index) => (event) => {
		let value = event.target.value;

		if (value.length > 1) {
			value = value.slice(-1);
		}

		if (value !== "" && !/^\d$/.test(value)) {
			return;
		}

		const pinKey = `pin_${index + 1}`;
		this.setState({[pinKey]: value}, () => {
			if (value !== "" && index < 3) {
				this.pinRefs[index + 1].current?.focus();
			}
		});
	};

	handlePinKeyDown = (index) => (event) => {
		const pinKey = `pin_${index + 1}`;
		if (event.key === "Backspace" && this.state[pinKey] === "" && index > 0) {
			this.pinRefs[index - 1].current?.focus();
		}
	};

	getFullPin = () => {
		return this.state.pin_1 + this.state.pin_2 + this.state.pin_3 + this.state.pin_4;
	};

	resetState = () => {
		this.setState({
			setupModalOpen: false,
			selectedForm: "",
			signaturePreference: DEFAULT_SIGNATURE_PREFERENCE,
			location: "",
			location_binary_id: "",
			location_error: "",
			pin_1: "",
			pin_2: "",
			pin_3: "",
			pin_4: "",
			error: "",
			hasAccountPin: false,
			isLoading: false
		});
	};

	startLockedRi060Session = async () => {
		const {selectedForm, location, location_binary_id, hasAccountPin, signaturePreference} = this.state;
		const {collection, collection_binary_id} = this.getCollectionIdentifiers();
		const routeId = getAcquisitionRouteId({collection, collection_binary_id});
		const pin = this.getFullPin();

		if (!routeId) {
			this.setState({
				error: "Missing transaction identifiers for kiosk mode",
				isLoading: false
			});
			return;
		}

		this.lockModel.object.kiosk_mode = ACQUISITION_KIOSK_MODE;
		this.lockModel.object.pin = hasAccountPin ? "" : pin;
		this.lockModel.object.location = location;
		this.lockModel.object.location_binary_id = location_binary_id;
		this.lockModel.object.acquisition_form_type = selectedForm;
		this.lockModel.object.acquisition_row_id = this.props.row?.id;
		this.lockModel.object.acquisition_row_pkey = this.props.row?.pkey;

		await this.lockModel.submit(null, "GET_LOCK");

		setAcquisitionKioskSession({
			collectionId: routeId,
			collection,
			collection_binary_id,
			routeId,
			lockedPath: buildAcquisitionKioskPath(routeId),
			returnPath: buildAcquisitionFormsPath(routeId),
			location,
			location_binary_id,
			selectedForm,
			signaturePreference,
			currentStep: "signing",
			hasSigned: false,
			hasAccountPin,
			source: "collection"
		});

		this.resetState();
		this.navigateTo(buildAcquisitionKioskPath(routeId));
	};

	handleSetupSubmit = async () => {
		const {selectedForm, location, hasAccountPin, signaturePreference} = this.state;
		const pin = this.getFullPin();
		const requiresSignature = selectedForm === "ri060" && signaturePreference !== "no_signature";

		this.setState({error: ""});

		if (selectedForm === "ri060" && this.isDealerEntity() && !this.isCustomerEntity()) {
			API.Toast.error("RI forms cannot be used for dealer acquisitions. Please use a customer acquisition instead.", {
				position: API.Toast.POSITION.TOP_RIGHT,
				autoClose: 5000
			});
			return;
		}

		if (!selectedForm) {
			this.setState({error: "Please select a form"});
			return;
		}

		if (!location) {
			this.setState({error: "Please select a location"});
			return;
		}

		if (requiresSignature && !hasAccountPin && pin.length !== 4) {
			this.setState({error: "Please enter a 4-digit PIN"});
			return;
		}

		try {
			this.setState({isLoading: true});

			if (selectedForm === "ri060" && !requiresSignature) {
				await this.createRi060Form();
				return;
			}

			await this.startLockedRi060Session();
		} catch (err) {
			this.setState({
				error: "Failed to lock device: " + (err.message || "Unknown error"),
				isLoading: false
			});
		}
	};

	renderPinInputs = (autoFocusFirst = false) => {
		return (
			<div className="d-flex justify-content-center gap-2">
				{[0, 1, 2, 3].map((index) => {
					const pinKey = `pin_${index + 1}`;
					return (
						<Input
							key={index}
							innerRef={this.pinRefs[index]}
							type="text"
							inputMode="numeric"
							maxLength={1}
							value={this.state[pinKey]}
							onChange={this.handlePinChange(index)}
							onKeyDown={this.handlePinKeyDown(index)}
							autoFocus={autoFocusFirst && index === 0}
							style={{
								width: "60px",
								height: "60px",
								fontSize: "24px",
								textAlign: "center",
								fontWeight: "bold"
							}}
						/>
					);
				})}
			</div>
		);
	};

	render() {
		const {
			setupModalOpen,
			selectedForm,
			signaturePreference,
			hasAccountPin,
			error,
			isLoading
		} = this.state;
		const requiresSignature = selectedForm === "ri060" && signaturePreference !== "no_signature";
		const actionLabel = requiresSignature
			? (isLoading ? "Locking..." : "Lock & Start")
			: (isLoading ? "Generating..." : "Generate");

		if (!this.hasRIModule()) {
			return null;
		}

		return (
			<div style={{position: "relative"}}>
				<Button
					color="primary"
					size="sm"
					block
					onClick={this.handleLockButtonClick}
					disabled={setupModalOpen || isLoading}
				>
					<i className="fa fa-lock"/> Kiosk
				</Button>

				<Modal isOpen={setupModalOpen} toggle={this.resetState} size="md">
					<ModalHeader toggle={this.resetState}>
						<i className="fa fa-file-text mr-2"/>
						Acquisition Form Setup
					</ModalHeader>
					<ModalBody>
						{error && (
							<div className="alert alert-danger">{error}</div>
						)}

						<FormGroup>
							<Label><strong>Select Form</strong></Label>
							<Input
								type="select"
								value={selectedForm}
								onChange={(event) => this.setState({selectedForm: event.target.value})}
							>
								<option value="">-- Select Form --</option>
								{FORM_OPTIONS.map((form) => (
									<option key={form.key} value={form.key}>{form.label}</option>
								))}
							</Input>
						</FormGroup>

						{selectedForm === "ri060" && (
							<FormGroup>
								<Label><strong>Customer Signature</strong></Label>
								<Input
									type="select"
									value={signaturePreference}
									onChange={(event) => this.setState({signaturePreference: event.target.value})}
								>
									{SIGNATURE_PREFERENCES.map((option) => (
										<option key={option.key} value={option.key}>{option.label}</option>
									))}
								</Input>
								<p className="text-muted small mb-0">
									Choose how the RI-060 prints the customer signature.
								</p>
							</FormGroup>
						)}

						<div className="form-group">
							<div className="row">
								<div className="col-4">
									<span className="input-label">Location</span>
								</div>
								<div className="col-8">
									<Framework.Elements.Select
										shouldLoad={true}
										update={this.handleSelectPropertyUpdate}
										objects={this.props.locations}
										property={this.lockModel.properties.location}
										value={this.state.location}
										binary_id={this.state.location_binary_id}
										error={this.state.location_error}
										text={"name_display|name"}
										filter={(option) => !option.archived}
									/>
								</div>
							</div>
						</div>

						{!hasAccountPin && requiresSignature && (
							<FormGroup>
								<Label><strong>Enter Lock PIN</strong></Label>
								<p className="text-muted small">
									This PIN will be required to unlock the device after signing.
								</p>
								{this.renderPinInputs(true)}
							</FormGroup>
						)}

						{hasAccountPin && requiresSignature && (
							<div className="alert alert-success">
								<i className="fa fa-info-circle mr-2"/>
								Using account PIN
							</div>
						)}
					</ModalBody>
					<ModalFooter>
						<Button color="secondary" onClick={this.resetState} disabled={isLoading}>
							Cancel
						</Button>
						<Button color="success" onClick={this.handleSetupSubmit} disabled={isLoading}>
							{actionLabel}
						</Button>
					</ModalFooter>
				</Modal>

				<Framework.Elements.LoadingSpinner isLoading={isLoading}/>
			</div>
		);
	}
}

export default Framework.Components.withRootState(FormPicker);
